import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    CircularProgress,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { createMenu, updateMenu, getMenuById } from '../../Services/RolesPermissions/MenuManagemnet/GetMenusPermissions';
import { useGlobalSnackbar } from "../../Components/Common/GlobalSanckbar";

const AddEditMenuPermissions = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // For edit mode
    const isEdit = Boolean(id);
    const { showSnackbar } = useGlobalSnackbar();

    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [isActive, setIsActive] = useState(true);

    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (isEdit) {
            const fetchMenu = async () => {
                try {
                    setLoading(true);
                    const response = await getMenuById(id);
                    if (response && response.success && response.data) {
                        let data = response.data;

                        // If data is an array, find the specific menu by ID from params
                        // If it's already an object (direct fetch), use it as is
                        if (Array.isArray(data)) {
                            data = data.find(item => item.id.toString() === id.toString());
                        }

                        if (data) {
                            setName(data.name || "");
                            setSlug(data.slug || "");
                            setBaseUrl(data.moduleType || data.module || data.baseUrl || "");
                            setIsActive(data.isActive ?? true);
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch menu", err);
                    showSnackbar("Failed to load menu data", "error");
                } finally {
                    setLoading(false);
                }
            };
            fetchMenu();
        }
    }, [id, isEdit, showSnackbar]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFieldErrors({});

        const menuData = {
            name,
            slug,
            moduleType: baseUrl,
            module: baseUrl,
            isActive
        };

        try {
            if (isEdit) {
                await updateMenu(id, menuData);
                showSnackbar("Menu updated successfully!", "success");
            } else {
                await createMenu(menuData);
                showSnackbar("Menu created successfully!", "success");
            }
            setTimeout(() => {
                navigate("/menu-permissions");
            }, 1500);
        } catch (err) {
            console.error("Failed to save menu", err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "Failed to save menu. Please try again.";

            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'general' ? 'name' : item.field;
                    errorsObj[fieldName] = item.message;
                });
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['name'] = serverError.errors[0];
            } else {
                errorsObj['name'] = errMsg;
            }
            setFieldErrors(errorsObj);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/menu-permissions");
    };

    return (
        <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#FBFBFB', minHeight: '85vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                    {isEdit ? "Edit Menu" : "Add Menu"}
                </Typography>
            </Box>

            <Paper sx={{ p: { xs: 3, md: 3.25 }, borderRadius: "12px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: '#444' }}>Menu Name *</Typography>
                            <TextField
                                fullWidth size="small" placeholder="Enter menu name"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (fieldErrors.name) {
                                        setFieldErrors(prev => ({ ...prev, name: '' }));
                                    }
                                }}
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '3px',
                                        bgcolor: '#fff'
                                    }
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: '#444' }}>Slug *</Typography>
                            <TextField
                                size="small" fullWidth placeholder="Enter slug (e.g., /menu)"
                                value={slug}
                                onChange={(e) => {
                                    setSlug(e.target.value);
                                    if (fieldErrors.slug) {
                                        setFieldErrors(prev => ({ ...prev, slug: '' }));
                                    }
                                }}
                                error={Boolean(fieldErrors.slug)}
                                helperText={fieldErrors.slug}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '3px',
                                        bgcolor: '#fff'
                                    }
                                }}
                            />
                        </Box>

                        {/* Base Url/Module */}
                        {/* <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: '#444' }}>Base URL / Module *</Typography>
                            <Autocomplete
                                fullWidth
                                options={availableModules}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.name || option.moduleType || option.baseUrl || "";
                                }}
                                value={availableModules.find(m => (m.moduleType || m.baseUrl || m) === baseUrl) || null}
                                onChange={(event, newValue) => {
                                    setBaseUrl(newValue ? (newValue.moduleType || newValue.baseUrl || newValue) : "");
                                    if (fieldErrors.moduleType || fieldErrors.module) {
                                        setFieldErrors(prev => ({ ...prev, moduleType: '', module: '' }));
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params} size="small"
                                        placeholder="Select or enter base URL"
                                        error={Boolean(fieldErrors.moduleType || fieldErrors.module)}
                                        helperText={fieldErrors.moduleType || fieldErrors.module}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '3px',
                                                bgcolor: '#fff'
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {modulesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                                disabled={modulesLoading}
                                noOptionsText="No modules available"
                            />
                        </Box> */}

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#FFBD65',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#FFBD65',
                                            },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontWeight: 500, color: '#444' }}>Status (Active)</Typography>}
                            />
                        </Box>

                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1.25, borderTop: '1px solid #F0F0F0' }}>
                            <Button
                                onClick={handleCancel}
                                disabled={loading}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    color: '#666',
                                    px: 4,
                                    py: 1,
                                    border: '1px solid #E8E8E8',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                disableElevation
                                sx={{
                                    bgcolor: '#FFBD65',
                                    color: '#fff',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1,
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                                ) : (
                                    isEdit ? "Update Menu" : "Create Menu"
                                )}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditMenuPermissions;
